import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import {
    AppStateFacade,
    UserAuthState,
} from '@app-state';
import {
    PageMetadataRegistry,
    PageScope,
} from '@app/router';
import { AccessCode } from '@models';
import { restrictedUrlTree } from '@pages/shared/restricted-access';

import {
    map,
    take,
} from 'rxjs/operators';

@Injectable()
export class PermissionGuard implements CanActivate {
    constructor(
        private _appState: AppStateFacade,
        private _pageRegistry: PageMetadataRegistry,
        private _router: Router,
    ) { }

    canActivate(
        _route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ) {
        const page = this._pageRegistry.findByRoute(state.url);

        if (!page || page.isWhitelisted) { return true; }

        if (
            page.scope === PageScope.Folder &&
            !this._appState.user.folder.hasAny
        ) {
            return restrictedUrlTree(this._router, {
                page,
                code: AccessCode.FolderRequired,
            });
        }

        if (!page.isAccessibleByFolder(this._appState.user.folder.type)) {
            return restrictedUrlTree(this._router, {
                page,
                code: AccessCode.ClassicFolderRequired,
            });
        }

        const authorized$ = this._appState.user.permissions
            .isGranted(page.permission)
            .pipe(
                take(1),
                // check if the user is authenticated
                map(isGranted =>
                    isGranted &&
                    this._appState.user.authState !== UserAuthState.NotLoggedIn,
                ),
                map(
                    isGranted => {
                        return isGranted || restrictedUrlTree(this._router, {
                            page,
                            code: AccessCode.PermissionRequired,
                        });
                    },
                ),
            );

        return authorized$;
    }
}
